import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import AuthModal from "../components/modal/AuthModal";
import useSEO from "../hooks/useSeo";

const PAGE_SIZE = 5;

const ReferralJobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [resume, setResume] = useState(null);
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  useSEO({
    title: "Referral Jobs | Get Developer Referrals & Earn Rewards",
    description:
      "Find the latest developer job referrals and exclusive openings. Apply through trusted referrals or share opportunities to earn rewards — only on SolveMyCodeIssue.com.",
    keywords:
      "referral jobs, developer referrals, tech job openings, earn rewards, software jobs, referral program, Solve My Code Issue",
    canonical: "https://solvemycodeissue.com/referral-jobs",
    ogImage: "https://solvemycodeissue.com/og-image-referral-jobs.png",
  });
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // ---------- Fetch Jobs ----------
  const fetchJobs = async (next = false) => {
    if (loading) return;
    setLoading(true);
    setIsSearching(false);

    try {
      let q = query(
        collection(db, "referralJobs"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );

      if (next && lastDoc) {
        q = query(
          collection(db, "referralJobs"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(PAGE_SIZE)
        );
      }

      const snapshot = await getDocs(q);
      const page = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      setHasMore(page.length === PAGE_SIZE);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setJobs((prev) => (next ? [...prev, ...page] : page));
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // ---------- Search ----------
  const runSearch = async (term) => {
    const qTerm = term.trim().toLowerCase();

    if (!qTerm) {
      setIsSearching(false);
      fetchJobs(false);
      return;
    }

    if (loading) return;
    setLoading(true);
    setIsSearching(true);
    setHasMore(false);

    try {
      const qTitle = query(
        collection(db, "referralJobs"),
        orderBy("jobTitleLower"),
        startAt(qTerm),
        endAt(qTerm + "\uf8ff"),
        limit(20)
      );
      const qCompany = query(
        collection(db, "referralJobs"),
        orderBy("companyNameLower"),
        startAt(qTerm),
        endAt(qTerm + "\uf8ff"),
        limit(20)
      );

      const [sTitle, sCompany] = await Promise.all([
        getDocs(qTitle),
        getDocs(qCompany),
      ]);

      const map = new Map();
      sTitle.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
      sCompany.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));

      const results = Array.from(map.values()).sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );

      setJobs(results);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ---------- Apply ----------
  const handleFileChange = (e) => setResume(e.target.files[0]);

  const handleSendReferral = async () => {
    if (!user?.id) {
      alert("Please login to send referral request!");
      navigate("/login");
      return;
    }

    if (!resume) {
      alert("Please upload your resume!");
      return;
    }

    try {
      setLoading(true);

      const storage = getStorage();
      const storageRef = ref(
        storage,
        `resumes/${user.id}/${Date.now()}_${resume.name}`
      );
      await uploadBytes(storageRef, resume);
      const downloadURL = await getDownloadURL(storageRef);

      const applicationData = {
        jobId: selectedJob.id,
        jobTitle: selectedJob.jobTitle,
        companyName: selectedJob.companyName,
        referralEmail: selectedJob.referralEmail,
        appliedById: user?.id,
        appliedByEmail: user?.email,
        appliedByName: user?.name,
        appliedAt: serverTimestamp(),
        resumeURL: downloadURL,
        status: "pending",
      };

      await addDoc(
        collection(db, "users", user.id, "jobApplications"),
        applicationData
      );

      if (selectedJob.postedById) {
        await addDoc(
          collection(
            db,
            "users",
            selectedJob.postedById,
            "receivedApplications"
          ),
          applicationData
        );
      }

      alert("Referral Request Sent Successfully!");
      setSelectedJob(null);
      setResume(null);
    } catch (err) {
      console.error("Error sending referral:", err);
      alert("Failed to send referral. Try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <JobsContainer>
        <Title>Referral Jobs</Title>

        {/* Search */}
        <SearchWrapper>
          <SearchBox
            type="text"
            placeholder="Search jobs by title or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchWrapper>

        {/* Jobs List */}
        {jobs.map((job) => (
          <JobCard key={job.id}>
            <JobHeader>
              <JobTitle>{job.jobTitle}</JobTitle>
              <CompanyName>{job.companyName}</CompanyName>
            </JobHeader>

            <JobInfo>
              <p>
                <strong>Location:</strong> {job.jobLocation}
              </p>
              <p>
                <strong>Experience:</strong> {job.experience}
              </p>
              <p>
                <strong>Description:</strong> {job.description}
              </p>
              <p>
                <strong>Referral Code:</strong> {job.referralCode}
              </p>
              <PostedBy>
                Posted by <span>{job.postedByName}</span> •{" "}
                {job.createdAt?.toDate
                  ? job.createdAt.toDate().toLocaleString()
                  : "N/A"}
              </PostedBy>
            </JobInfo>

            {/* User check */}
            {user?.id ? (
              user.id !== job.postedById && (
                <ApplyButton onClick={() => setSelectedJob(job)}>
                  Send Referral Request
                </ApplyButton>
              )
            ) : (
              <LoginButton onClick={() => navigate("/login")}>
                Login to Apply
              </LoginButton>
            )}
          </JobCard>
        ))}

        {!loading && jobs.length === 0 && <EmptyMsg>No jobs found.</EmptyMsg>}

        {!isSearching && hasMore && (
          <LoadMoreButton onClick={() => fetchJobs(true)} disabled={loading}>
            {loading ? "Loading..." : "Load More"}
          </LoadMoreButton>
        )}
      </JobsContainer>

      {/* Modal */}
      {selectedJob && (
        <ModalOverlay>
          <ModalContent>
            <h3>Apply for {selectedJob.jobTitle}</h3>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
            />
            <ModalActions>
              <CancelButton onClick={() => setSelectedJob(null)}>
                Cancel
              </CancelButton>
              <SendButton onClick={handleSendReferral} disabled={loading}>
                {loading ? "Sending..." : "Send"}
              </SendButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      <FloatingButton
        onClick={() => {
          if (user) navigate("/post-referral-job");
          else setShowAuthModal(true);
        }}
      >
        <Plus size={16} />
        Post Referral Job
      </FloatingButton>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </PageWrapper>
  );
};

export default ReferralJobsPage;

/* ---------------- Improved UI (Glassy Theme + User Check) ---------------- */
const FloatingButton = styled.button`
  position: fixed;
  bottom: 26px;
  right: 26px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  padding: 12px 16px;
  border-radius: 999px;
  display: flex;
  gap: 10px;
  align-items: center;
  border: none;
  font-weight: 700;
  box-shadow: 0 8px 30px rgba(155, 92, 255, 0.2);
  cursor: pointer;
  z-index: 2000;

  &:hover {
    transform: translateY(-3px);
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 40px 20px;
  background: radial-gradient(circle at top left, #0b0617, #000);
  display: flex;
  justify-content: center;
`;

const JobsContainer = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const Title = styled.h2`
  text-align: center;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 700;
  margin-bottom: 24px;
`;

const SearchWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  width: 100%;
`;

const SearchBox = styled.input`
  width: 100%;
  max-width: 550px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(155, 92, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    border: 1px solid #9b5cff;
    box-shadow: 0 0 12px rgba(155, 92, 255, 0.4);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const JobCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  color: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(14px);
  transition: 0.3s all ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 45px rgba(155, 92, 255, 0.25);
  }
`;

const JobHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const JobTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const CompanyName = styled.span`
  font-size: 1rem;
  color: #bfbfbf;
`;

const JobInfo = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #ddd;
  margin-top: 12px;
  margin-bottom: 14px;

  strong {
    color: #c59fff;
  }
`;

const PostedBy = styled.p`
  font-size: 0.8rem;
  color: #aaa;
  margin-top: 10px;

  span {
    color: #fff;
    font-weight: 600;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.3s ease;
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(155, 92, 255, 0.35);
  }
`;

const LoginButton = styled(ApplyButton)`
  background: rgba(255, 255, 255, 0.08);
  color: #c59fff;
  font-weight: 500;
  border: 1px solid rgba(155, 92, 255, 0.3);

  &:hover {
    background: rgba(155, 92, 255, 0.2);
  }
`;

const LoadMoreButton = styled(ApplyButton)`
  margin-top: 30px;
  font-size: 1rem;
`;

const EmptyMsg = styled.p`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 20px 0;
`;

/* ---------- Modal ---------- */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  color: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
`;

const SendButton = styled(CancelButton)`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.25);
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(155, 92, 255, 0.35);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.15);
    cursor: not-allowed;
  }
`;
